import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Avatar } from '../components/Avatar';
import { getInterviewByToken } from '../api/interview';
import type { InterviewPublic } from '../api/types';

export default function PreCheck() {
  const { token } = useParams<{ token: string }>();
  const nav = useNavigate();
  const [info, setInfo] = useState<InterviewPublic | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [micTested, setMicTested] = useState(false);
  const [camTested, setCamTested] = useState(false);
  const [consent, setConsent] = useState(false);

  useEffect(() => {
    if (!token) return;
    getInterviewByToken(token).then(setInfo).catch((e) => setError(e.message));
  }, [token]);

  const testMedia = async () => {
    setError(null);
    try {
      if (!navigator.mediaDevices?.getUserMedia) {
        throw new Error('浏览器不支持 getUserMedia(非安全上下文?)');
      }

      // 先列出所有设备,诊断用
      const devs = await navigator.mediaDevices.enumerateDevices();
      const cams = devs.filter((d) => d.kind === 'videoinput');
      const mics = devs.filter((d) => d.kind === 'audioinput');
      console.log('[PreCheck] devices:', { cams, mics, all: devs });
      if (cams.length === 0 && mics.length === 0) {
        throw new Error('系统里一个摄像头和麦克风都没检测到(可能 macOS 隐私设置里相机权限没给浏览器)');
      }

      // 分开申请,更容易定位哪一端出问题
      let audioStream: MediaStream | null = null;
      let videoStream: MediaStream | null = null;
      try {
        audioStream = await navigator.mediaDevices.getUserMedia({ audio: true });
        setMicTested(true);
      } catch (e: any) {
        console.warn('[PreCheck] audio only failed:', e.name, e.message);
      }
      try {
        videoStream = await navigator.mediaDevices.getUserMedia({ video: true });
        setCamTested(true);
      } catch (e: any) {
        console.warn('[PreCheck] video only failed:', e.name, e.message);
      }

      // 清理
      [audioStream, videoStream].forEach((s) => s?.getTracks().forEach((t) => t.stop()));

      if (!audioStream && !videoStream) {
        throw new Error('音视频都申请失败,见控制台');
      }
    } catch (e: any) {
      const name = e?.name || 'Error';
      let hint = e?.message || String(e);
      if (name === 'NotAllowedError') hint = '浏览器授权被拒,点击地址栏左边的锁🔒图标重新允许';
      else if (name === 'NotFoundError') hint = '没找到摄像头/麦克风硬件';
      else if (name === 'NotReadableError') hint = '设备被其他应用占用(Zoom / FaceTime / 微信等),先关掉它们';
      else if (name === 'OverconstrainedError') hint = `设备不支持请求参数:${e.message}`;
      setError(`设备授权失败 (${name}):${hint}`);
      console.error('[PreCheck] getUserMedia failed:', e);
    }
  };

  if (!info && error) return <div className="container"><div className="banner error">{error}</div></div>;
  if (!info) return <div className="container"><p className="hint">加载面试信息...</p></div>;

  // 麦克风是硬需求(AI 要听到你);摄像头可选(本项目 AI 不看画面)
  const readyToStart = micTested && consent;

  return (
    <div className="container">
      <h1>准备进入面试</h1>
      {error && <div className="banner error" style={{ marginBottom: 16 }}>{error}</div>}
      <div className="card">
        {/* 面试官名片(候选人只看头像 + 姓名)*/}
        <div className="row" style={{ gap: 12, marginBottom: 16, padding: 12, background: 'var(--bg-elev)', borderRadius: 8 }}>
          <Avatar value={info.interviewer_avatar} name={info.interviewer_name} size={48} />
          <div>
            <div className="hint">面试官</div>
            <div style={{ fontSize: 16, fontWeight: 600, marginTop: 2 }}>{info.interviewer_name || '李先生'}</div>
          </div>
        </div>

        <p><b>候选人:</b>{info.candidate_name || '(未命名)'}</p>
        <p><b>岗位:</b>{info.job_title}</p>
        <p><b>开始时间:</b>{new Date(info.scheduled_at).toLocaleString('zh-CN')}</p>
        <p><b>时长:</b>约 {info.duration_minutes} 分钟</p>
      </div>

      <div className="card">
        <h2>① 设备检测</h2>
        <div className="col">
          <button onClick={testMedia}>测试摄像头和麦克风</button>
          <div className="hint">
            状态:麦克风 {micTested ? '✅ 已就绪' : '⏳ 未测试'}(必需) · 摄像头 {camTested ? '✅' : '➖'}(可选)
          </div>
          {micTested && !camTested && (
            <div className="hint" style={{ color: 'var(--success)' }}>
              ✅ 麦克风 OK,可以进入面试间
            </div>
          )}
        </div>
      </div>

      <div className="card">
        <h2>② 合规告知</h2>
        <div className="banner info">
          本次面试由 <b>{info.interviewer_name || '李先生'}</b> 进行,
          全程录音用于后续评估。录音保留 90 天,超期自动删除。点击"我同意"即代表你理解并同意上述内容。
        </div>
        <label style={{ display: 'block', marginTop: 8 }}>
          <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} />
          &nbsp;我同意上述合规要求
        </label>
      </div>

      <div className="card">
        <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
          <button disabled={!readyToStart} onClick={() => nav(`/interview/${token}/room`)}>
            进入面试间
          </button>
        </div>
        {!consent && <p className="hint">至少要勾选"我同意"才能继续</p>}
        {consent && !micTested && (
          <p className="hint">请先测试麦克风</p>
        )}
      </div>
    </div>
  );
}
